import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { Confirm24 } from 'ui/icons/action/Confirm24';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';

import { useModalContext } from '../contexts/ModalContext';
import { useQueryContext } from '../contexts/QueryContext';
import { deleteListErrorId } from '../errorIds';
import { useDeleteListMutation } from '../hooks/useDeleteListMutation';

const displayText = {
  error: 'Error deleting list',
  confirm: 'Yes',
  cancel: 'No',
};

const Bold = styled.span`
  font-weight: bold;
`;

const StyledModalHeader = styled(ModalHeader)`
  > div {
    display: flex;
    align-items: center;
    gap: ${spacing.half};
  }
`;

export const DeleteListModal = () => {
  const dispatch = useDispatch();
  const { listToDelete, hideModal } = useModalContext();
  const { refetch, listSizes } = useQueryContext();
  const {
    deleteList,
    isLoading: isDeleting,
    isSuccess: didDelete,
    isError: couldNotDelete,
    error,
  } = useDeleteListMutation(listToDelete);

  // Effect: Handle submission
  useEffect(() => {
    if (isDeleting || !listToDelete) return;
    if (didDelete) {
      dispatch(
        addPositiveToast(
          <>
            <Bold>{listToDelete.name}</Bold> was successfully deleted
          </>,
        ),
      );
      refetch();
      hideModal();
    } else if (couldNotDelete) {
      dispatch(addNegativeToastWithErrorId(displayText.error, deleteListErrorId, error));
      hideModal();
    }
  }, [isDeleting, didDelete, couldNotDelete]);

  if (!listToDelete) return null;

  return (
    <Modal id="deleteListModal" isModalOpen onClose={hideModal}>
      <StyledModalHeader>
        <Confirm24 />
        Please Confirm
      </StyledModalHeader>
      <ModalBody>
        Are you sure you want to delete list <Bold>{listToDelete.name}</Bold>
        {listSizes[listToDelete.id] !== undefined && (
          <>
            {' '}
            with <Bold>{listSizes[listToDelete.id]} users</Bold>
          </>
        )}
        ?
      </ModalBody>
      <ModalFooter hasCancel={false}>
        <SecondaryButton onClick={deleteList} data-test="confirmDeleteList">
          {displayText.confirm}
        </SecondaryButton>
        <PrimaryButton onClick={hideModal} data-test="cancelDeleteList">
          {displayText.cancel}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
